import {
  Alert,
  Badge,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import type { ClientConfig } from "@sc07-canvas/lib/net";
import { useCallback, useEffect, useState } from "react";

import { useAppContext } from "@/contexts/AppContext";
import { useDialog } from "@/contexts/DialogContext";
import { useCountdown } from "@/hooks/useCountdown";
import network from "@/lib/network";

import OpenChatButton from "../Chat/OpenChatButton";
import { Button } from "../core/Button";
import { LemmyButton } from "../Info/buttons/Lemmy";
import { MailingListsButton } from "../Info/buttons/MailingLists";
import { MastodonButton } from "../Info/buttons/Mastodon";
import { WithLogin } from "../Login/LoginButton";
import { useEventDates } from "./useEventDates";

export const EventInfoModal = () => {
  const Dialogs = useDialog();
  const { isOpen, onClose } = useDisclosure({
    isOpen: "EVENT_INFO" in Dialogs.state.dialogs,
    onClose: () =>
      Dialogs.dispatch({ action: "CLOSE_DIALOG", dialogId: "EVENT_INFO" }),
  });
  const date = useEventDates();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={false}
      placement="center"
    >
      <ModalContent className="canvas-overlay-card">
        {(onClose) => (
          <>
            <ModalHeader>Canvas 2026</ModalHeader>
            <ModalBody>
              {date.start && Date.now() < date.start ? (
                <>
                  <h1 className="text-3xl text-center font-bold">
                    {date.start ? <TimeUntil start={date.start} /> : "..."}
                  </h1>
                  <h2 className="text-2xl text-center">until Canvas 2026</h2>
                </>
              ) : (
                <>
                  <h1 className="text-3xl text-center font-bold">
                    {date.end ? <TimeUntil start={date.end} /> : "..."}
                  </h1>
                  <h2 className="text-2xl text-center">left for Canvas 2026</h2>
                </>
              )}
              <p>
                Canvas is a collaborative pixel canvas that runs every year for{" "}
                <Link href="https://fediverse.info/" target="_blank">
                  the Fediverse
                </Link>
                !
              </p>

              <EventHype />

              <Alert
                color="primary"
                variant="faded"
                classNames={{ title: "font-semibold" }}
                title="Are you a Fediverse developer?"
                description={
                  <>
                    You can now integrate your app with Canvas!
                    <br />
                    <Button
                      as={Link}
                      color="primary"
                      size="sm"
                      variant="flat"
                      target="_blank"
                      href="https://sc07.dev/fediverse.events/fediverse.events-api/-/wikis/home"
                    >
                      View Docs
                    </Button>
                  </>
                }
              />

              <div className="flex gap-1 items-center justify-center flex-wrap">
                <OpenChatButton size="sm" target="_blank" variant="ghost" />
                <LemmyButton />
                <MastodonButton />
                <MailingListsButton />
              </div>

              <Alert
                color="secondary"
                variant="faded"
                classNames={{ title: "font-semibold" }}
                title="Interested in contributing?"
                description={
                  <>
                    Canvas is open-source and is accepting contributions
                    <br />
                    <Button
                      as={Link}
                      color="secondary"
                      size="sm"
                      variant="flat"
                      target="_blank"
                      href="https://sc07.dev/sc07/canvas"
                    >
                      Check out the source
                    </Button>
                  </>
                }
              />
            </ModalBody>
            <ModalFooter>
              <Button onPress={onClose}>View the Canvas!</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const TimeUntil = ({ start }: { start: number }) => {
  const { timeLeft } = useCountdown(start, true);

  return <>{timeLeft}</>;
};

const EventHype = () => {
  const { config, user } = useAppContext();
  const [data, setData] = useState<{ [emoji: string]: number }>({});

  useEffect(() => {
    fetch("/api/event/hype")
      .then((a) => a.json())
      .then((data) => {
        setData(data);
      });
  }, []);

  useEffect(() => {
    const handle = (emoji: string, count: number) => {
      setData((current) => ({ ...current, [emoji]: count }));
    };

    network.socket.on("event_hype", handle);

    return () => {
      network.socket.off("event_hype", handle);
    };
  }, []);

  if (!config?.event.hype.enable) return undefined;

  if (user) return <EventHypeInner counts={data} config={config} />;

  return (
    <WithLogin>
      {(doLogin) => (
        <EventHypeInner counts={data} config={config} doLogin={doLogin} />
      )}
    </WithLogin>
  );
};

const EventHypeInner = ({
  config,
  counts,
  doLogin,
}: {
  config: ClientConfig;
  counts: { [emoji: string]: number };
  doLogin?: () => void;
}) => {
  const { emojis } = config.event.hype;
  const [loading, setLoading] = useState(false);

  const doReact = useCallback((emoji: string) => {
    if (doLogin) return doLogin();

    setLoading(true);

    fetch("/api/event/hype", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        emoji,
      }),
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  return (
    <>
      <div className="flex flex-row gap-1 items-center justify-center">
        {emojis.map((emoji) => (
          <Badge key={emoji} color="primary" content={counts[emoji] ?? "?"}>
            <Button
              variant="bordered"
              className="min-w-0 text-2xl p-3"
              size="lg"
              onPress={() => doReact(emoji)}
              isLoading={loading}
            >
              {emoji}
            </Button>
          </Badge>
        ))}
      </div>
      <span className="text-sm *:text-sm text-center">
        Use this to test logging in!{" "}
        <Link href={MAILTO_FEDIAUTH_ISSUES}>Having issues?</Link>
      </span>
    </>
  );
};

const MAILTO_FEDIAUTH_ISSUES =
  "mailto:canvas@sc07.dev?" +
  new URLSearchParams({
    subject: "fediverse-auth Issue",
    body: "What is your Fediverse handle? (eg. @grant@grants.cafe)\nWhat issue did you encounter?",
  });
